import { useParams, Link } from "wouter";
import { useState, useEffect } from "react";
import {
  useGetPage,
  useGetPageWarnings,
  useGetPageLinks,
  useRecrawlPage,
  useUpdatePageKeywords,
  useGetPageLinkSuggestions,
  useResolveWarning,
  useRejectWarning,
  getGetPageQueryKey,
  getGetPageWarningsQueryKey,
  getGetPageLinksQueryKey,
  getGetPageLinkSuggestionsQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Link as LinkIcon,
  ExternalLink,
  CheckCircle2,
  X,
  Plus,
  Save,
  Lightbulb,
  Tag,
} from "lucide-react";

const WARNING_LABELS: Record<string, string> = {
  ORPHAN_PAGE: "Sahipsiz sayfa",
  BROKEN_LINK: "Kırık link",
  REDIRECT_LINK: "Yönlendirmeli link",
  MISSING_CLUSTER_LINK: "Cluster içi gövde linki eksik",
  EXCESSIVE_LINKS: "Aşırı sayıda link",
  DUPLICATE_ANCHOR: "Yinelenen anchor",
  SAME_PAGE_MULTIPLE: "Aynı sayfaya çoklu link",
  NOFOLLOW_IMBALANCE: "NoFollow / DoFollow dengesizliği",
  SEO_INCOMPATIBLE_ANCHOR: "SEO uyumsuz anchor",
  EXACT_MATCH_OVERUSE: "Exact match anchor aşırı kullanımı",
  UNRELATED_LINK: "Alakasız sayfaya link",
  DEEP_LINK_DEPTH: "Aşırı link derinliği",
  NO_BIDIRECTIONAL: "Çift yönlü linkleme yok",
  AI_IRRELEVANT_LINK: "AI: Alakasız iç link",
  AI_WEAK_ANCHOR: "AI: Anchor metni zayıf",
  AI_MISSING_LINK: "AI: Eksik iç link önerisi",
  AI_OVER_OPTIMIZED_ANCHOR: "AI: Aşırı optimize anchor",
  AI_GENERIC_ANCHOR: "AI: Jenerik anchor metni",
  AI_OFF_TOPIC_CLUSTER: "AI: Cluster konusuyla uyumsuz",
  TOPIC_ORPHAN: "Konu Orphan (cluster içi gövde linki yok)",
  INSUFFICIENT_INTRA_CLUSTER_LINKS: "Cluster içi yetersiz link",
  KEYWORD_CONFLICT: "Anahtar kelime çakışması",
};

function warningLabel(type: string): string {
  return WARNING_LABELS[type] ?? type;
}

function renderInlineMarkdownLinks(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const [, label, url] = match;
    out.push(
      <a
        key={`mdlink-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-emerald-700 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900 hover:decoration-emerald-600 font-medium"
      >
        {label}
      </a>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length > 0 ? out : [text];
}

function positionBadgeLabel(pos: string | undefined): string | null {
  switch (pos) {
    case "footer":
      return "Footer / Servis";
    case "nav":
      return "Menü";
    case "header":
      return "Header";
    default:
      return null;
  }
}

function KeywordChips({
  values,
  onChange,
  placeholder,
  testIdPrefix,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  testIdPrefix: string;
}) {
  const [draft, setDraft] = useState("");

  const addCurrent = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.length === 0 && (
          <span className="text-xs text-muted-foreground italic">Henüz eklenmedi</span>
        )}
        {values.map((kw, i) => (
          <Badge key={`${kw}-${i}`} variant="secondary" className="gap-1 pr-1" data-testid={`${testIdPrefix}-chip-${i}`}>
            {kw}
            <button
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
              aria-label="Kaldır"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addCurrent();
            }
          }}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button type="button" variant="outline" size="icon" onClick={addCurrent} aria-label="Ekle">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function PageDetail() {
  const { id } = useParams<{ id: string }>();
  const pageId = parseInt(id, 10);

  const { data: page, isLoading: pageLoading } = useGetPage(pageId, {
    query: { enabled: !!pageId, queryKey: getGetPageQueryKey(pageId) },
  });
  const { data: warnings, isLoading: warningsLoading } = useGetPageWarnings(pageId, {
    query: { enabled: !!pageId, queryKey: getGetPageWarningsQueryKey(pageId) },
  });
  const { data: links, isLoading: linksLoading } = useGetPageLinks(pageId, {
    query: { enabled: !!pageId, queryKey: getGetPageLinksQueryKey(pageId) },
  });
  const { data: suggestionsData } = useGetPageLinkSuggestions(pageId, {
    query: { enabled: !!pageId, queryKey: getGetPageLinkSuggestionsQueryKey(pageId) },
  });

  const recrawlPage = useRecrawlPage();
  const updateKeywords = useUpdatePageKeywords();
  const resolveWarning = useResolveWarning();
  const rejectWarning = useRejectWarning();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateWarningsAndScore = () => {
    if (!pageId) return;
    queryClient.invalidateQueries({ queryKey: getGetPageWarningsQueryKey(pageId) });
    queryClient.invalidateQueries({ queryKey: getGetPageQueryKey(pageId) });
  };

  const handleAcceptWarning = (warningId: number) => {
    resolveWarning.mutate(
      { warningId },
      {
        onSuccess: () => {
          invalidateWarningsAndScore();
          toast({ title: "Öneri kabul edildi", description: "Uyarı çözüldü olarak işaretlendi." });
        },
        onError: () => toast({ title: "Hata", description: "Öneri kabul edilemedi.", variant: "destructive" }),
      },
    );
  };

  const handleRejectWarning = (warningId: number) => {
    rejectWarning.mutate(
      { warningId },
      {
        onSuccess: () => {
          invalidateWarningsAndScore();
          toast({ title: "Öneri reddedildi", description: "Bu öneri bu domain için bir daha gösterilmeyecek." });
        },
        onError: () => toast({ title: "Hata", description: "Öneri reddedilemedi.", variant: "destructive" }),
      },
    );
  };

  const isAiWarning = (warningType: string) => warningType.startsWith("AI_");

  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState<string[]>([]);
  const [target, setTarget] = useState<string[]>([]);
  const [keywordError, setKeywordError] = useState<string | null>(null);
  const [keywordsDirty, setKeywordsDirty] = useState(false);

  useEffect(() => {
    if (!page) return;
    setPrimary(page.primaryKeyword ?? "");
    setSecondary((page.secondaryKeywords as string[]) ?? []);
    setTarget((page.targetKeywords as string[]) ?? []);
    setKeywordsDirty(false);
    setKeywordError(null);
  }, [page?.id, page?.primaryKeyword]);

  const markDirty = () => {
    setKeywordsDirty(true);
    setKeywordError(null);
  };

  const handleSaveKeywords = async () => {
    setKeywordError(null);
    try {
      await updateKeywords.mutateAsync({
        pageId,
        data: {
          primaryKeyword: primary.trim() || null,
          secondaryKeywords: secondary,
          targetKeywords: target,
        },
      });
      toast({ title: "Anahtar kelimeler kaydedildi" });
      setKeywordsDirty(false);
      queryClient.invalidateQueries({ queryKey: getGetPageQueryKey(pageId) });
      queryClient.invalidateQueries({ queryKey: getGetPageLinkSuggestionsQueryKey(pageId) });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setKeywordError(
          "Bu anahtar kelime başka bir sayfada birincil olarak kullanılıyor. Lütfen farklı bir kelime seçin.",
        );
      } else {
        setKeywordError("Kaydedilemedi. Lütfen tekrar deneyin.");
      }
    }
  };

  // Cluster haritası: kendi cluster'ı + üst cluster'daki sayfalar, link ilişkileriyle
  type ClusterPageRow = { id: number; url: string; title: string | null; score: number | null; ilr: number | null; isFeatured: boolean; isPillar: boolean; primaryKeyword: string | null; incomingCount: number; linkedByMe: boolean; linksToMe: boolean };
  type ClusterPagesResp = { cluster: { id: number; name: string } | null; parent: { id: number; name: string } | null; siblings: ClusterPageRow[]; parentPages: ClusterPageRow[] };
  const { data: clusterPages } = useQuery({
    queryKey: ["cluster-pages", pageId],
    enabled: !!pageId,
    queryFn: async (): Promise<ClusterPagesResp> => {
      try { const r = await fetch(`/api/pages/${pageId}/cluster-pages`); return r.ok ? await r.json() : { cluster: null, parent: null, siblings: [], parentPages: [] }; }
      catch { return { cluster: null, parent: null, siblings: [], parentPages: [] }; }
    },
  });

  // "Detaylı AI ile Tarat": sayfa canlı fetch edilir → kural motoru (26+ SEO/GEO kuralı)
  // → Opus ile derin AI analizi (uyarılar + link inşa planı). SADECE kullanıcı tetikler —
  // eski "sayfa açılınca otomatik dene" davranışı kaldırıldı (kredi yokken sonsuz
  // boş dönüşe ve gizli maliyete sebep oluyordu).
  const handleRecrawl = async () => {
    try {
      const r = (await recrawlPage.mutateAsync({ pageId })) as unknown as {
        message?: string;
        aiError?: string | null;
      };
      if (r?.aiError) {
        toast({ title: r?.message ?? "Kural taraması tamamlandı", description: r.aiError, variant: "destructive" });
      } else {
        toast({ title: r?.message ?? "Detaylı tarama tamamlandı" });
      }
      queryClient.invalidateQueries({ queryKey: getGetPageQueryKey(pageId) });
      queryClient.invalidateQueries({ queryKey: getGetPageWarningsQueryKey(pageId) });
      queryClient.invalidateQueries({ queryKey: getGetPageLinksQueryKey(pageId) });
      queryClient.invalidateQueries({ queryKey: getGetPageLinkSuggestionsQueryKey(pageId) });
    } catch {
      toast({ title: "Tarama başarısız — sayfa indirilemedi veya sunucu hatası", variant: "destructive" });
    }
  };

  if (pageLoading) return <div className="p-8">Yükleniyor...</div>;
  if (!page) return <div className="p-8">Sayfa bulunamadı.</div>;

  // Katı eşikler (kural motoruyla hizalı): yeşil ≥85, amber 55-84, kırmızı <55.
  const scoreHex = (score: number | null | undefined) => {
    if (score === null || score === undefined) return "#a3a3a0";
    if (score < 55) return "#d92d20";
    if (score < 85) return "#d97706";
    return "#16a34a";
  };

  // Halka gösterge: değer 0-100, renkli yay + ortada sayı (grafiksel skor görünümü)
  const ScoreRing = ({ value, color, label, hint }: { value: number | null | undefined; color: string; label: string; hint: string }) => {
    const v = value ?? 0;
    const R = 26;
    const C = 2 * Math.PI * R;
    return (
      <div className="flex flex-col items-center" title={hint}>
        <div className="relative w-[72px] h-[72px]">
          <svg width="72" height="72" viewBox="0 0 72 72">
            <circle cx="36" cy="36" r={R} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="7" />
            <circle
              cx="36" cy="36" r={R} fill="none"
              stroke={value === null || value === undefined ? "rgba(0,0,0,0.15)" : color}
              strokeWidth="7" strokeLinecap="round"
              strokeDasharray={`${(v / 100) * C} ${C}`}
              transform="rotate(-90 36 36)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-xl font-bold" style={{ color }}>
            {value ?? "-"}
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link href={`/domains/${page.domainId}`}>
            <Button variant="ghost" size="icon" className="mt-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90 break-all">{page.title || "İsimsiz Sayfa"}</h1>
            <a
              href={page.url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 flex items-center gap-1 break-all text-theme-sm text-gray-500 hover:text-brand-500 dark:text-gray-400"
            >
              {page.url}
              <ExternalLink className="w-3 h-3" />
            </a>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {page.clusterName && <Badge variant="secondary">{page.clusterName}</Badge>}
              {page.primaryKeyword && (
                <Badge variant="outline" className="gap-1">
                  <Tag className="w-3 h-3" />
                  {page.primaryKeyword}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <ScoreRing
            value={(page as { ilr?: number | null }).ilr}
            color="#e83e3c"
            label="ILR"
            hint="ILR (Internal LinkRank): iç link grafiğinden PageRank — sitenin link gücünün bu sayfaya ne kadar aktığı (ana sayfa ≈ 100). Site analiziyle güncellenir."
          />
          <ScoreRing
            value={page.score}
            color={scoreHex(page.score)}
            label="Skor"
            hint="Link inşası skoru: kural motorunun katı puanı (İyi ≥85, Orta 55-84, Kritik <55)."
          />
          <Button onClick={handleRecrawl} disabled={recrawlPage.isPending} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${recrawlPage.isPending ? "animate-spin" : ""}`} />
            {recrawlPage.isPending ? "Detaylı Analiz Sürüyor…" : "Detaylı AI ile Tarat"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="warnings" className="w-full">
        <TabsList className="h-auto w-full justify-start gap-1 rounded-none border-b border-gray-200 bg-transparent p-0 dark:border-gray-800">
          <TabsTrigger
            value="warnings"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            Uyarılar ({warnings?.length || 0})
          </TabsTrigger>
          <TabsTrigger
            value="keywords"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            Anahtar Kelimeler
          </TabsTrigger>
          <TabsTrigger
            value="links"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            Linkler ({links?.length || 0})
          </TabsTrigger>
          <TabsTrigger
            value="cluster"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            Cluster ({(clusterPages?.siblings.length ?? 0) + (clusterPages?.parentPages.length ?? 0)})
          </TabsTrigger>
          <TabsTrigger
            value="suggestions"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            Link Önerileri ({suggestionsData?.suggestions?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger
            value="ai"
            className="rounded-none border-b-2 border-transparent px-5 py-3 text-theme-sm font-medium text-gray-500 data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:text-brand-500 dark:text-gray-400"
          >
            AI Önerisi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="warnings" className="pt-6">
          {warningsLoading ? (
            <div className="text-center py-8">Yükleniyor...</div>
          ) : warnings && warnings.length > 0 ? (
            <div className="space-y-4">
              {warnings.map((warning) => (
                <Card
                  key={warning.id}
                  className={
                    warning.severity === "critical"
                      ? "border-red-200"
                      : warning.severity === "medium"
                      ? "border-amber-200"
                      : "border-blue-200"
                  }
                >
                  <CardContent className="p-4 flex gap-4">
                    <AlertTriangle
                      className={`w-5 h-5 shrink-0 ${
                        warning.severity === "critical"
                          ? "text-red-500"
                          : warning.severity === "medium"
                          ? "text-amber-500"
                          : "text-blue-500"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">{warningLabel(warning.warningType)}</div>
                      <p className="text-sm text-muted-foreground">{warning.message}</p>
                      {warning.anchorText && (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="bg-muted/60 px-2 py-1 rounded font-mono">
                            Anchor: <span className="font-semibold">{warning.anchorText}</span>
                          </span>
                        </div>
                      )}
                      {warning.targetUrl && (
                        <div className="mt-2 text-xs bg-muted/50 p-2 rounded break-all font-mono text-muted-foreground">
                          Hedef: {warning.targetUrl}
                        </div>
                      )}
                      {warning.originalParagraph && (
                        <div className="mt-3">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Mevcut paragraf
                          </div>
                          <div className="text-sm bg-rose-50 border border-rose-200 text-rose-950 p-3 rounded whitespace-pre-wrap leading-relaxed">
                            {warning.originalParagraph}
                          </div>
                        </div>
                      )}
                      {warning.suggestedParagraph && (
                        <div className="mt-3">
                          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                            Önerilen düzenleme (SEO + GEO uyumlu)
                          </div>
                          <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-950 p-3 rounded whitespace-pre-wrap leading-relaxed">
                            {renderInlineMarkdownLinks(warning.suggestedParagraph)}
                          </div>
                        </div>
                      )}
                      {isAiWarning(warning.warningType) && (
                        <div className="mt-4 flex items-center gap-2 pt-3 border-t border-dashed">
                          <span className="text-xs text-muted-foreground mr-2">AI Önerisi:</span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 border-emerald-200"
                            disabled={resolveWarning.isPending || rejectWarning.isPending}
                            onClick={() => handleAcceptWarning(warning.id)}
                            title="Öneriyi kabul et — uyarı çözüldü olarak işaretlenir"
                          >
                            <span className="text-base leading-none">✅</span>
                            Kabul Et
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-rose-700 hover:bg-rose-50 hover:text-rose-800 border-rose-200"
                            disabled={resolveWarning.isPending || rejectWarning.isPending}
                            onClick={() => handleRejectWarning(warning.id)}
                            title="Öneriyi reddet — bu domain için bir daha önerilmez"
                          >
                            <span className="text-base leading-none">❌</span>
                            Reddet
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-lg font-medium">Harika! Uyarı yok.</h3>
                <p className="text-muted-foreground mt-2">Bu sayfa iç linkleme açısından kusursuz görünüyor.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="keywords" className="pt-6">
          <Card>
            <CardHeader>
              <CardTitle>Anahtar Kelimeler</CardTitle>
              <CardDescription>
                Birincil anahtar kelime site genelinde tek bir sayfaya ait olabilir. Hedef anahtar kelimeler bu sayfaya
                gelen iç link önerilerinde önceliklendirilir.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="primary-keyword" className="mb-2 block">
                  Birincil Anahtar Kelime
                </Label>
                <Input
                  id="primary-keyword"
                  value={primary}
                  onChange={(e) => {
                    setPrimary(e.target.value);
                    markDirty();
                  }}
                  placeholder="örn. seo iç linkleme"
                  data-testid="primary-keyword-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Bu kelime aynı domain içindeki başka bir sayfanın birincil anahtarıyla çakışamaz.
                </p>
              </div>

              <div>
                <Label className="mb-2 block">İkincil Anahtar Kelimeler</Label>
                <KeywordChips
                  values={secondary}
                  onChange={(v) => {
                    setSecondary(v);
                    markDirty();
                  }}
                  placeholder="Enter ile ekle"
                  testIdPrefix="secondary"
                />
              </div>

              <div>
                <Label className="mb-2 block">Hedef Anahtar Kelimeler</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Bu sayfaya gönderilmesini istediğiniz anchor metinleri. Link önerilerinde önceliklendirilir.
                </p>
                <KeywordChips
                  values={target}
                  onChange={(v) => {
                    setTarget(v);
                    markDirty();
                  }}
                  placeholder="Enter ile ekle"
                  testIdPrefix="target"
                />
              </div>

              {keywordError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                  {keywordError}
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveKeywords}
                  disabled={!keywordsDirty || updateKeywords.isPending}
                  className="gap-2"
                  data-testid="save-keywords"
                >
                  <Save className="w-4 h-4" />
                  Kaydet
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cluster" className="pt-6 space-y-6">
          {(() => {
            if (!clusterPages?.cluster) {
              return <div className="text-center py-10 text-muted-foreground">Bu sayfa henüz bir cluster'a atanmadı.</div>;
            }
            const ClusterTable = ({ rows, title, subtitle }: { rows: ClusterPageRow[]; title: string; subtitle: string }) => (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{title}</CardTitle>
                  <CardDescription>{subtitle}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {rows.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Bu cluster'da başka sayfa yok.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="text-left font-medium px-4 py-2">Sayfa</th>
                          <th className="text-right font-medium px-3 py-2" title="Internal LinkRank — site içi otorite">ILR</th>
                          <th className="text-right font-medium px-3 py-2">Skor</th>
                          <th className="text-right font-medium px-3 py-2" title="Sayfanın site genelinden aldığı gövde linki sayısı">Aldığı Link</th>
                          <th className="text-center font-medium px-3 py-2" title="Bu sayfa gövdesinden ona link veriyor muyum?">Ben → O</th>
                          <th className="text-center font-medium px-4 py-2" title="O sayfanın gövdesinden bana link geliyor mu?">O → Ben</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                            <td className="px-4 py-2.5 max-w-[440px]">
                              <Link href={`/pages/${r.id}`} className="text-primary hover:underline block truncate" title={r.url}>
                                {r.title || r.url}
                              </Link>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-[11px] text-muted-foreground truncate max-w-[280px]">{r.url}</span>
                                {r.isPillar && <Badge variant="default" className="text-[10px] px-1.5 py-0">Pillar</Badge>}
                                {r.isFeatured && !r.isPillar && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Öne Çıkan</Badge>}
                                {r.primaryKeyword && <span className="text-[10px] text-muted-foreground">· {r.primaryKeyword}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-indigo-600">{r.ilr ?? "-"}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold" style={{ color: scoreHex(r.score) }}>{r.score ?? "-"}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{r.incomingCount}</td>
                            <td className="px-3 py-2.5 text-center">{r.linkedByMe ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-500" title="Gövdeden link verilmiyor — link inşası fırsatı">✗</span>}</td>
                            <td className="px-4 py-2.5 text-center">{r.linksToMe ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-500" title="Bu sayfadan gövde linki alınmıyor — link inşası fırsatı">✗</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            );
            return (
              <>
                <ClusterTable
                  rows={clusterPages.siblings}
                  title={`Bu Sayfanın Cluster'ı: ${clusterPages.cluster.name}`}
                  subtitle="Aynı konu ailesindeki kardeş sayfalar — en güçlü link kaynakları/hedefleri bunlar. ✗ olan hücreler kaçırılan cluster içi link fırsatlarıdır."
                />
                {clusterPages.parent && (
                  <ClusterTable
                    rows={clusterPages.parentPages}
                    title={`Üst Cluster: ${clusterPages.parent.name}`}
                    subtitle="Hiyerarşide bir üst konu ailesi — pillar/hub sayfalarından bu sayfaya inen linkler otorite taşır."
                  />
                )}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="links" className="pt-6 space-y-6">
          {(() => {
            // Gelen Anchor Profili (Ahrefs mantığı): bu sayfaya gövdeden gelen linklerin
            // anchor dağılımı — aşırı exact-match doğal görünmez, hiç exact yoksa hedefleme zayıf.
            type AnchorBucket = { key: string; label: string; count: number; pct: number; examples: string[] };
            const profile = (page as { anchorProfile?: { total: number; buckets: AnchorBucket[] } }).anchorProfile;
            if (!profile || profile.total === 0) return null;
            const COLORS: Record<string, string> = {
              exact: "#16a34a", partial: "#0ea5e9", brand: "#e83e3c",
              generic: "#f79009", url: "#fb6514", empty: "#a3a3a0", other: "#70706c",
            };
            return (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Gelen Anchor Profili</CardTitle>
                  <CardDescription>
                    Bu sayfaya gövde metninden gelen {profile.total} linkin anchor dağılımı. Sağlıklı profil: exact + kısmi ağırlıklı, jenerik az.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.buckets.map((b) => (
                    <div key={b.key} className="flex items-center gap-3">
                      <div className="w-44 shrink-0 text-sm">{b.label}</div>
                      <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                        <div className="h-3 rounded-full" style={{ width: `${Math.max(3, b.pct)}%`, background: COLORS[b.key] ?? "#70706c" }} />
                      </div>
                      <div className="w-20 shrink-0 text-right text-sm tabular-nums">{b.count} · %{b.pct}</div>
                      <div className="w-64 shrink-0 truncate text-xs text-muted-foreground" title={b.examples.join(" · ")}>
                        {b.examples.length > 0 ? `örn: ${b.examples.join(" · ")}` : ""}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })()}
          <Card>
            {linksLoading ? (
              <div className="p-8 text-center">Yükleniyor...</div>
            ) : links && links.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hedef URL</TableHead>
                    <TableHead>Anchor Text</TableHead>
                    <TableHead className="w-24">Nofollow</TableHead>
                    <TableHead className="w-40">Bağlam</TableHead>
                    <TableHead className="w-32">Konum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {links.map((link) => {
                    const posLabel = positionBadgeLabel(link.linkPosition);
                    return (
                      <TableRow key={link.id}>
                        <TableCell className="max-w-[300px] truncate" title={link.targetUrl}>
                          <a href={link.targetUrl} target="_blank" rel="noreferrer" className="hover:underline text-primary">
                            {link.targetUrl}
                          </a>
                        </TableCell>
                        <TableCell className="font-medium">
                          {link.linkType === "image" ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300">
                                  Image
                                </Badge>
                                <span className="text-xs text-muted-foreground">Image Link</span>
                              </div>
                              <span className="text-sm">
                                {link.imageAlt && link.imageAlt.trim() ? (
                                  link.imageAlt
                                ) : (
                                  <em className="text-muted-foreground">Alt metin bulunamadı</em>
                                )}
                              </span>
                            </div>
                          ) : (
                            link.anchorText || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {link.isNofollow ? (
                            <Badge variant="secondary">Evet</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Hayır</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {link.isContextual ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              İçerik İçi
                            </Badge>
                          ) : (
                            <Badge variant="outline">Menü/Footer</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {posLabel ? (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              {posLabel}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">İçerik</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center text-muted-foreground">
                <LinkIcon className="w-8 h-8 mx-auto mb-3 opacity-50" />
                Bu sayfadan çıkan iç link bulunamadı.
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="suggestions" className="pt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Önerilen İç Linkler
              </CardTitle>
              <CardDescription>
                Bu sayfaya verilebilecek en alakalı iç linkler. Hedef anahtar kelimeler ve aynı cluster üyelikleri
                önceliklendirilir.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {suggestionsData && suggestionsData.suggestions.length > 0 ? (
                <div className="space-y-3">
                  {suggestionsData.suggestions.map((s) => (
                    <div
                      key={s.id}
                      className="p-4 border rounded-lg hover:bg-muted/30 transition flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Link href={`/pages/${s.id}`} className="font-medium hover:underline truncate">
                            {s.title || s.url}
                          </Link>
                          {s.inSameCluster && (
                            <Badge variant="secondary" className="text-xs">
                              Aynı cluster
                            </Badge>
                          )}
                          {s.primaryKeyword && (
                            <Badge variant="outline" className="text-xs gap-1">
                              <Tag className="w-3 h-3" />
                              {s.primaryKeyword}
                            </Badge>
                          )}
                        </div>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-muted-foreground hover:text-primary break-all"
                        >
                          {s.url}
                        </a>
                        <p className="text-xs text-muted-foreground mt-1">{s.reason}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-muted-foreground">Uyum</div>
                        <div className="text-lg font-semibold">{Math.round(s.score)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Şu anda öneri bulunamadı. Hedef anahtar kelime ekleyerek öneri kalitesini artırabilirsiniz.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="pt-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>AI Analizi</CardTitle>
                  <CardDescription>Bu sayfa için yapay zeka tarafından oluşturulan iyileştirme önerileri.</CardDescription>
                </div>
                {((page as { linkPlan?: unknown[] }).linkPlan?.length ?? 0) > 0 && (
                  <Link href={`/page-strengthen/${pageId}?applyPlan=1`}>
                    <Button
                      className="gap-2 shrink-0"
                      title="Kayıtlı link inşa planını Sayfa Güçlendirme revizesine öncelikli olarak enjekte eder"
                    >
                      Link Planını Güçlendirmede Uygula
                    </Button>
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {page.aiSuggestion ? (
                <div
                  className="prose prose-sm max-w-none prose-p:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: page.aiSuggestion }}
                />
              ) : recrawlPage.isPending ? (
                <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Detaylı AI analizi sürüyor… (uyarılar + link inşa planı üretiliyor)
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Henüz AI analizi yok. "Detaylı AI ile Tarat" butonuyla bu sayfa için derin link analizi (uyarılar + link inşa planı) üretebilirsiniz.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
